export type UserRole = "mitglied" | "shopmanager" | "administrator";
export type UserStatus = "aktiv" | "passiv" | "inaktiv";

export interface RegisterUserPayload {
  kleidlesnummer: string;
  email: string;
  name: string;
  vorname: string;
  passwort: string;
}

export interface AdminCreateUserPayload extends RegisterUserPayload {
  role: UserRole;
  status: UserStatus;
}

export interface UserDto {
  id: string;
  kleidlesnummer: string;
  role: UserRole;
  status: UserStatus;
  email: string;
  name: string;
  vorname: string;
  createdAtUtc: string;
}

export interface LoginPayload {
  email: string;
  passwort: string;
}

export interface AdminUpdateUserPayload {
  kleidlesnummer: string;
  role: UserRole;
  status: UserStatus;
  email: string;
  name: string;
  vorname: string;
}

export interface AdminResetUserPasswordPayload {
  neuesPasswort: string;
}

export interface ChangeOwnPasswordPayload {
  aktuellesPasswort: string;
  neuesPasswort: string;
}

export interface LoginResponseDto {
  token: string;
  expiresAtUtc: string;
  user: UserDto;
}

export interface RequestPasswordResetPayload {
  email: string;
}

export interface ResetPasswordPayload {
  token: string;
  neuesPasswort: string;
}

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? "http://localhost:5124";

export async function registerUser(payload: RegisterUserPayload): Promise<UserDto> {
  const response = await fetch(`${API_BASE_URL}/api/users/register`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => null);
    throw new Error(error?.detail ?? "Benutzer konnte nicht angelegt werden.");
  }

  return response.json();
}

export async function createUserAsAdmin(token: string, payload: AdminCreateUserPayload): Promise<UserDto> {
  const response = await fetch(`${API_BASE_URL}/api/users/admin`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => null);
    throw new Error(error?.detail ?? "Benutzer konnte nicht angelegt werden.");
  }

  return response.json();
}

export async function loginUser(payload: LoginPayload): Promise<LoginResponseDto> {
  const response = await fetch(`${API_BASE_URL}/api/users/login`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => null);
    throw new Error(error?.detail ?? "Anmeldung fehlgeschlagen.");
  }

  return response.json();
}

export async function requestPasswordReset(payload: RequestPasswordResetPayload): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/users/password-reset/request`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => null);
    throw new Error(error?.detail ?? "Passwort-Reset konnte nicht angefordert werden.");
  }
}

export async function resetPassword(payload: ResetPasswordPayload): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/users/password-reset/confirm`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => null);
    throw new Error(error?.detail ?? "Passwort konnte nicht zur�ckgesetzt werden.");
  }
}

export async function getUsersForAdmin(token: string): Promise<UserDto[]> {
  const response = await fetch(`${API_BASE_URL}/api/users/admin`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => null);
    throw new Error(error?.detail ?? "Benutzer konnten nicht geladen werden.");
  }

  return response.json();
}

export async function updateUserAsAdmin(token: string, userId: string, payload: AdminUpdateUserPayload): Promise<UserDto> {
  const response = await fetch(`${API_BASE_URL}/api/users/admin/${userId}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => null);
    throw new Error(error?.detail ?? "Benutzer konnte nicht aktualisiert werden.");
  }

  return response.json();
}

export async function resetUserPasswordAsAdmin(
  token: string,
  userId: string,
  payload: AdminResetUserPasswordPayload
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/users/admin/${userId}/password`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => null);
    throw new Error(error?.detail ?? "Passwort konnte nicht aktualisiert werden.");
  }
}

export async function changeOwnPassword(token: string, payload: ChangeOwnPasswordPayload): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/users/me/password`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => null);
    throw new Error(error?.detail ?? "Passwort konnte nicht aktualisiert werden.");
  }
}

export async function deleteUserAsAdmin(token: string, userId: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/users/admin/${userId}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => null);
    throw new Error(error?.detail ?? "Benutzer konnte nicht gel�scht werden.");
  }
}
